/**
 * «¿Cómo lo lleva?»: la unidad suelta, la tira o la caja entera.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ SE PREGUNTA, Y SOLO AQUÍ
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Solo sale en los productos que el dueño configuró con caja o tira. El resto
 * del catálogo sigue entrando de un toque, como siempre.
 *
 * Y el lector no pasa por aquí: el código de la caja es otro que el de la
 * lata, así que escaneando la caja entra la caja directo. Esto es para quien
 * vende tocando la pantalla, que es justo quien no tiene cómo decirlo de otra
 * forma.
 *
 * Cada opción dice lo que el cajero necesita para contestarle al cliente: el
 * precio, a cuánto sale cada una dentro de la caja, y si alcanza.
 *
 * ⚠️ Con el teclado, 1, 2, 3: el cajero rápido no suelta el teclado.
 */

import { useEffect } from "react";
import { Boxes, Package } from "lucide-react";

import { Modal } from "@/components/Modal";
import { cx } from "@/components/ui";
import { formatMoney, formatQty, type FormatoMoneda } from "@/lib/money";
import type { ItemListRow } from "@/lib/types";
import { etiquetaDeUnidad } from "./pesoYDinero";
import {
  cuantasAlcanzan,
  cuantoSeAhorra,
  precioPorUnidad,
  unidadesQueTrae,
} from "./presentaciones";

type Props = {
  producto: ItemListRow;
  presentaciones: ItemListRow[];
  formato: FormatoMoneda;
  /** Lo suelto: sigue su camino de siempre (al peso pregunta cuánto). */
  onSuelto: () => void;
  onCaja: (caja: ItemListRow) => void;
  onCerrar: () => void;
};

export function SelectorDePresentacion({
  producto,
  presentaciones,
  formato,
  onSuelto,
  onCaja,
  onCerrar,
}: Props) {
  const unidad = etiquetaDeUnidad(producto.unit);

  // 1 = suelto, 2… = las cajas, en el orden en que se ven.
  useEffect(() => {
    function alTeclear(e: KeyboardEvent) {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      // Lo que se escribe en una casilla es de la casilla: un código tecleado
      // en el buscador no puede elegir la caja por llevar un 2 dentro.
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      const n = Number.parseInt(e.key, 10);
      if (!Number.isInteger(n) || n < 1) return;
      if (n === 1) {
        e.preventDefault();
        onSuelto();
      } else if (presentaciones[n - 2]) {
        e.preventDefault();
        onCaja(presentaciones[n - 2]!);
      }
    }
    window.addEventListener("keydown", alTeclear);
    return () => window.removeEventListener("keydown", alTeclear);
  }, [presentaciones, onSuelto, onCaja]);

  const hay = producto.existencia == null ? null : Number(producto.existencia);

  return (
    <Modal abierto titulo={producto.name} descripcion="¿Cómo lo lleva?" onCerrar={onCerrar} ancho="sm">
      <div className="grid gap-2">
        <Opcion
          atajo={1}
          icono={<Package size={18} />}
          titulo={unidad ? `Suelto, por ${unidad}` : "Una unidad"}
          precio={formatMoney(producto.price_with_tax, formato)}
          detalle="Precio normal"
          quedan={hay === null ? null : `Quedan ${formatQty(hay)}${unidad ? ` ${unidad}` : ""}`}
          alcanza={hay === null || hay > 0}
          onClick={onSuelto}
        />

        {presentaciones.map((caja, i) => {
          const cuantas = cuantasAlcanzan(producto, caja);
          const ahorro = cuantoSeAhorra(producto, caja);
          const trae = unidadesQueTrae(caja);
          return (
            <Opcion
              key={caja.id}
              atajo={i + 2}
              icono={<Boxes size={18} />}
              titulo={`${caja.presentacion_nombre ?? "Caja"} ×${formatQty(trae)}${unidad ? ` ${unidad}` : ""}`}
              precio={formatMoney(caja.price_with_tax, formato)}
              detalle={
                `${formatMoney(precioPorUnidad(caja), formato)} ${unidad ? `el ${unidad}` : "c/u"}` +
                (ahorro !== null ? ` · ${Math.round(ahorro * 100)} % menos` : "")
              }
              quedan={
                cuantas === null
                  ? null
                  : cuantas === 0
                    ? "No alcanza"
                    : `Alcanza para ${formatQty(cuantas)}`
              }
              alcanza={cuantas === null || cuantas > 0}
              onClick={() => onCaja(caja)}
            />
          );
        })}
      </div>
    </Modal>
  );
}

function Opcion({
  atajo,
  icono,
  titulo,
  precio,
  detalle,
  quedan,
  alcanza,
  onClick,
}: {
  atajo: number;
  icono: React.ReactNode;
  titulo: string;
  precio: string;
  detalle: string;
  quedan: string | null;
  alcanza: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        // 56 px de alto: se toca con el pulgar, de pie y con prisa.
        "group flex min-h-14 w-full items-center gap-3 rounded-[var(--radius-control)] border p-3 text-left transition-colors",
        "border-line bg-surface hover:border-accent hover:bg-accent-soft",
        "focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30",
      )}
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-[calc(var(--radius-control)-2px)] bg-accent-soft text-accent">
        {icono}
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-ink">{titulo}</span>
        <span className="tabular block truncate text-xs text-muted">{detalle}</span>
      </span>

      <span className="shrink-0 text-right">
        <span className="tabular block text-base font-semibold leading-tight text-ink">{precio}</span>
        {quedan && (
          <span
            className={cx(
              "tabular block text-[11px] leading-tight",
              alcanza ? "text-muted" : "font-semibold text-danger",
            )}
          >
            {quedan}
          </span>
        )}
      </span>

      {/* El atajo de teclado, solo en la computadora. */}
      <kbd className="hidden rounded border border-b-2 border-line-strong bg-surface-2 px-1.5 py-0.5 font-sans text-[11px] font-semibold text-muted sm:block">
        {atajo}
      </kbd>
    </button>
  );
}
